import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import Config from 'src/common/Config';
import { DocumentVersion } from './useDocumentVersions';

const API_URL = Config.getApiUrl();

type VersionsData = { data: DocumentVersion[] };
type PreviousDataEntry = [QueryKey, VersionsData | undefined];

export function useSetPrimaryVersion(documentId?: string) {
  const queryClient = useQueryClient();

  const matchesVersionsQuery = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey.some(
      key => typeof key === 'string' && key.includes(`documents/${documentId}/versions`),
    );

  const { mutateAsync, error, isLoading, reset } = useMutation({
    mutationFn: async (versionId: string) => {
      if (!documentId) throw new Error('documentId is required');
      const response = await fetch(`${API_URL}/documents/${documentId}/set-as-primary`, {
        method: 'POST',
        body: JSON.stringify({ documentVersionId: versionId }),
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.status < 200 || response.status > 299) throw response;
    },
    onMutate: async (versionId: string) => {
      await queryClient.cancelQueries({ predicate: matchesVersionsQuery });

      const previousData = queryClient.getQueriesData<VersionsData>({
        predicate: matchesVersionsQuery,
      });

      queryClient.setQueriesData<VersionsData>(
        { predicate: matchesVersionsQuery },
        (old: VersionsData | undefined) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map(v => ({ ...v, isPrimary: v.id === versionId })),
          };
        },
      );

      return { previousData };
    },
    onError: (
      _err: unknown,
      _versionId: string,
      context: { previousData: PreviousDataEntry[] } | undefined,
    ) => {
      context?.previousData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ predicate: matchesVersionsQuery });
    },
  });

  return {
    setPrimaryVersion: mutateAsync,
    error,
    isLoading,
    reset,
  };
}
